/**
 * StyledTextField Input Adornments Hook
 *
 * Handles input adornments with proper event handling and theme integration.
 * IMPORTANT: This file fixes the critical bug where clicks on endAdornment buttons
 * (like password visibility toggles) were not working properly.
 *
 * Key fixes:
 * - Proper event propagation for IconButton clicks
 * - Theme-aware styling for adornments
 * - Proper handling of both startAdornment and endAdornment props
 * - Support for InputProps override pattern
 */

import React, { useMemo } from 'react';
import { InputAdornment, useTheme } from '@mui/material';
import type { StyledTextFieldProps, InputAdornmentsResult } from './StyledTextField.types';

// ============================================================================
// INPUT ADORNMENTS HOOK
// ============================================================================

export const useInputAdornments = (props: StyledTextFieldProps): InputAdornmentsResult => {
  const {
    startAdornment,
    endAdornment,
    InputProps = {}
  } = props;

  const theme = useTheme();

  // ============================================================================
  // ADORNMENT PROCESSING
  // ============================================================================

  const inputAdornments = useMemo(() => {
    // Start with any existing InputProps
    const adornments: any = { ...InputProps };

    // Handle startAdornment - only if not already in InputProps
    if (startAdornment && !InputProps.startAdornment) {
      adornments.startAdornment = (
        <InputAdornment
          position="start"
          sx={{
            color: theme.palette.text.secondary,
            // Ensure icons are properly styled
            '& .MuiSvgIcon-root': {
              color: 'inherit',
            },
          }}
        >
          {startAdornment}
        </InputAdornment>
      );
    }

    // Handle endAdornment - only if not already in InputProps
    if (endAdornment && !InputProps.endAdornment) {
      adornments.endAdornment = (
        <InputAdornment
          position="end"
          sx={{
            color: theme.palette.text.secondary,

            // CRITICAL: Ensure click events work properly
            '& .MuiIconButton-root': {
              padding: theme.spacing(0.5),
              color: theme.palette.text.secondary,

              // Ensure button is clickable and has proper states
              cursor: 'pointer',
              userSelect: 'none',

              // Proper focus and hover states
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.text.primary,
              },

              '&:focus': {
                backgroundColor: theme.palette.action.focus,
                outline: 'none',
              },

              '&:active': {
                backgroundColor: theme.palette.action.selected,
              },

              // Ensure the button doesn't interfere with field interaction
              '&:focus-within': {
                outline: 'none',
              },
            },

            // Ensure icons within buttons are properly styled
            '& .MuiSvgIcon-root': {
              color: 'inherit',
              fontSize: theme.typography.pxToRem(20),
            },
          }}
        >
          {endAdornment}
        </InputAdornment>
      );
    }

    // Apply styling to any existing InputProps adornments
    if (InputProps.endAdornment || InputProps.startAdornment) {
      // Add global styling for adornments that come from InputProps
      const adornmentStyles = {
        // CRITICAL: Ensure click events work properly for IconButtons
        '& .MuiIconButton-root': {
          padding: theme.spacing(0.5),
          color: theme.palette.text.secondary,

          // Ensure button is clickable and has proper states
          cursor: 'pointer',
          userSelect: 'none',

          // Proper focus and hover states
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.primary,
          },

          '&:focus': {
            backgroundColor: theme.palette.action.focus,
            outline: 'none',
          },

          '&:active': {
            backgroundColor: theme.palette.action.selected,
          },
        },

        // Ensure icons within buttons are properly styled
        '& .MuiSvgIcon-root': {
          color: 'inherit',
          fontSize: theme.typography.pxToRem(20),
        },
      };

      // Merge adornment styles into the sx prop
      adornments.sx = {
        ...adornments.sx,
        ...adornmentStyles,
      };
    }

    return adornments;
  }, [startAdornment, endAdornment, InputProps, theme]);

  // ============================================================================
  // RETURN HOOK RESULT
  // ============================================================================

  return inputAdornments;
};

// ============================================================================
// ADORNMENT UTILITIES
// ============================================================================

/**
 * Create a password visibility toggle button
 * This is a common use case that was previously broken
 */
export const createPasswordToggle = (
  isVisible: boolean,
  onToggle: () => void,
  VisibilityIcon: React.ComponentType,
  VisibilityOffIcon: React.ComponentType
) => {
  // Import IconButton locally to avoid circular dependencies
  const { IconButton } = require('@mui/material');

  return (
    <IconButton
      onClick={(e: React.MouseEvent) => {
        // CRITICAL: Prevent event from bubbling to the field
        e.stopPropagation();
        e.preventDefault();
        onToggle();
      }}
      onMouseDown={(e: React.MouseEvent) => {
        // CRITICAL: Prevent focus from being lost from the input
        e.preventDefault();
      }}
      edge="end"
      aria-label={isVisible ? 'Hide password' : 'Show password'}
      size="small"
      tabIndex={-1} // Remove from tab order to keep focus on input
    >
      {isVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
    </IconButton>
  );
};

/**
 * Create a clear/reset button for search fields
 */
export const createClearButton = (
  onClear: () => void,
  ClearIcon: React.ComponentType,
  disabled = false
) => {
  const { IconButton } = require('@mui/material');

  return (
    <IconButton
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        onClear();
      }}
      onMouseDown={(e: React.MouseEvent) => {
        e.preventDefault();
      }}
      edge="end"
      aria-label="Clear field"
      size="small"
      disabled={disabled}
      tabIndex={-1}
    >
      <ClearIcon />
    </IconButton>
  );
};

/**
 * Create a submit button for search fields
 */
export const createSubmitButton = (
  onSubmit: () => void,
  SubmitIcon: React.ComponentType,
  disabled = false
) => {
  const { IconButton } = require('@mui/material');

  return (
    <IconButton
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        onSubmit();
      }}
      onMouseDown={(e: React.MouseEvent) => {
        e.preventDefault();
      }}
      edge="end"
      aria-label="Submit"
      size="small"
      disabled={disabled}
      tabIndex={-1}
    >
      <SubmitIcon />
    </IconButton>
  );
};

// ============================================================================
// ADORNMENT PATTERNS
// ============================================================================

/**
 * Common patterns for adornments
 */
export const AdornmentPatterns = {

  /**
   * Search field with clear button
   */
  searchWithClear: (value: string, onClear: () => void, SearchIcon: any, ClearIcon: any) => ({
    startAdornment: <SearchIcon />,
    endAdornment: value ? createClearButton(onClear, ClearIcon) : null,
  }),

  /**
   * Password field with toggle
   */
  passwordWithToggle: (
    isVisible: boolean,
    onToggle: () => void,
    LockIcon: any,
    VisibilityIcon: any,
    VisibilityOffIcon: any
  ) => ({
    startAdornment: <LockIcon />,
    endAdornment: createPasswordToggle(isVisible, onToggle, VisibilityIcon, VisibilityOffIcon),
  }),

  /**
   * URL field with validation indicator
   */
  urlWithIndicator: (isValid: boolean, UrlIcon: any, CheckIcon: any, ErrorIcon: any) => ({
    startAdornment: <UrlIcon />,
    endAdornment: isValid ? <CheckIcon color="success" /> : <ErrorIcon color="error" />,
  }),

};